import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, ArrowLeft, Upload, Link2, Check, Clapperboard, UserRound } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { downscaleToDataUrl } from '../lib/img';
import { toast } from '../hooks/use-toast';

const inputCls = 'w-full rounded-md border border-[#2A2A30] bg-[#141417] px-3 py-2 text-sm text-white placeholder:text-white/25 focus:border-white/40 focus:outline-none';

// What the scenario needs, said in one word on the card so the creator can see
// before clicking whether they have it to hand.
const NEEDS_LABEL = { photo: 'a photo', url: 'a link', none: 'nothing' };

export default function UgcTemplates({ onClose }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [assets, setAssets] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pick, setPick] = useState(null);
  const [assetId, setAssetId] = useState('');
  const [url, setUrl] = useState('');
  const [influencerId, setInfluencerId] = useState('');
  const [script, setScript] = useState('');
  const [uploading, setUploading] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    Promise.all([api.listUgcTemplates(), api.listAssets(), api.listInfluencers()])
      .then(([t, a, i]) => {
        setTemplates(t?.templates || []);
        // Styles are the look, not the thing being sold, so they would only be
        // noise in a picker that is asking what to advertise.
        setAssets((a || []).filter((x) => x.type !== 'style'));
        setInfluencers(i || []);
      })
      .catch((e) => toast({ title: 'Could not load the templates', description: e.message }))
      .finally(() => setLoading(false));
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await downscaleToDataUrl(file, 1280);
      const a = await api.createAsset({
        type: 'product', name: file.name.replace(/\.[^.]+$/, '').slice(0, 60), dataUrl,
      });
      const withUrl = { ...a, url: a.url || (a.file_key ? `/api/files/${a.file_key}` : null) };
      setAssets((prev) => [withUrl, ...prev]);
      setAssetId(a.id);
    } catch (e) {
      toast({ title: 'Could not upload that image', description: e.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.startUgcTemplate({
        slug: pick.slug,
        assetId: pick.needs === 'photo' ? (assetId || null) : null,
        url: pick.needs === 'url' ? url.trim() : null,
        influencerId: influencerId || null,
        script: script.trim() || null,
      });
      onClose?.(); // navigating to another studio session reuses the component
      navigate(`/app/studio/${r.workflowId}`);
    } catch (e) {
      // Capturing a site can fail for reasons the creator can act on, so the
      // message from the server is shown rather than a generic failure.
      toast({ title: 'Could not start that template', description: e.message });
      setStarting(false);
    }
  };

  // Only the input the scenario actually needs blocks the button. Everything
  // else on the second step is optional on purpose.
  const ready = pick && (
    pick.needs === 'photo' ? !!assetId
      : pick.needs === 'url' ? /\./.test(url.trim())
        : true);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-[#2A2A30] bg-[#141417]"
        onClick={(ev) => ev.stopPropagation()} data-testid="ugc-templates">

        <div className="flex items-center justify-between border-b border-[#2A2A30] px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Clapperboard size={18} className="text-node-style" />
            <h3 className="font-serif-display text-lg text-white">
              {pick ? pick.label : 'UGC templates'}
            </h3>
            {pick && <span className="text-xs text-white/40">{pick.duration}s · {pick.aspect}</span>}
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-white/40" /></div>
          ) : !pick ? (
            <>
              <p className="mb-4 text-sm text-white/60">
                Pick what you are making. The scene, the styling and the shot are already set,
                so all that is left is the one thing only you have.
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {templates.map((t) => (
                  <button key={t.slug} onClick={() => { setPick(t); setScript(''); }}
                    data-testid={`ugc-template-${t.slug}`}
                    className="rounded-xl border border-[#2A2A30] bg-[#0E0E10] p-4 text-left transition-colors hover:border-white/25">
                    <span className="block text-sm font-medium text-white">{t.label}</span>
                    <span className="mt-1 block text-xs leading-snug text-white/45">{t.blurb}</span>
                    <span className="mt-2 inline-block rounded-full border border-[#2A2A30] px-2 py-0.5 text-[10px] text-white/40">
                      you bring {NEEDS_LABEL[t.needs]}
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {pick.needs === 'photo' && (
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">
                    {pick.input_hint}
                  </label>
                  <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-6">
                    <button onClick={() => fileRef.current?.click()} disabled={uploading}
                      data-testid="ugc-upload"
                      className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#2A2A30] text-white/40 hover:border-white/30 hover:text-white/70 disabled:opacity-50">
                      {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      <span className="text-[10px]">Upload</span>
                    </button>
                    {assets.map((a) => (
                      <button key={a.id} onClick={() => setAssetId(a.id)} title={a.name}
                        className={`relative aspect-square overflow-hidden rounded-lg border ${assetId === a.id ? 'border-node-style' : 'border-[#2A2A30]'}`}>
                        {a.url
                          ? <img src={fileUrl(a.url)} alt={a.name} className="h-full w-full object-cover" />
                          : <span className="flex h-full items-center justify-center px-1 text-[9px] text-white/40">{a.name}</span>}
                        {assetId === a.id && (
                          <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-node-style">
                            <Check size={10} className="text-black" />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(ev) => upload(ev.target.files?.[0])} />
                  {assets.length === 0 && !uploading && (
                    <p className="mt-2 text-[11px] text-white/35">Nothing in your library yet. Upload the photo here.</p>
                  )}
                </div>
              )}

              {pick.needs === 'url' && (
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">
                    {pick.input_hint}
                  </label>
                  <div className="mt-1 flex items-center gap-2 rounded-md border border-[#2A2A30] bg-[#141417] px-3">
                    <Link2 size={14} className="shrink-0 text-white/30" />
                    <input value={url} onChange={(ev) => setUrl(ev.target.value)}
                      data-testid="ugc-url" placeholder="acme.com"
                      className="w-full bg-transparent py-2 text-sm text-white placeholder:text-white/25 focus:outline-none" />
                  </div>
                  <p className="mt-1 text-[11px] text-white/35">
                    We capture the page ourselves and put it on the screen in the shot.
                  </p>
                </div>
              )}

              {influencers.length > 0 && (
                <div>
                  <label className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-white/50">
                    <UserRound size={12} /> Presenter, optional
                  </label>
                  <select value={influencerId} onChange={(ev) => setInfluencerId(ev.target.value)}
                    data-testid="ugc-influencer" className={`${inputCls} mt-1`}>
                    <option value="">Let the scenario pick someone</option>
                    {influencers.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">
                  Script, optional
                </label>
                <textarea rows={3} value={script} onChange={(ev) => setScript(ev.target.value)}
                  data-testid="ugc-script" placeholder={pick.script_hint || 'Leave blank and we write one for you.'}
                  className={`${inputCls} mt-1 resize-none`} />
                <p className="mt-1 text-[11px] text-white/35">
                  Leave it blank and the scenario's own direction is used. You can rewrite it
                  before rendering either way.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-[#2A2A30] px-5 py-3.5">
          {pick ? (
            <button onClick={() => setPick(null)}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2A30] px-4 py-2 text-sm text-white/70 hover:text-white">
              <ArrowLeft size={14} /> All templates
            </button>
          ) : <span />}
          {pick && (
            <button onClick={start} disabled={!ready || starting} data-testid="ugc-start"
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black disabled:opacity-40">
              {starting ? <Loader2 size={15} className="animate-spin" /> : <Clapperboard size={15} />}
              {starting && pick.needs === 'url' ? 'Capturing the page…' : 'Build it'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
